/**
 * B03030 請求書出力
 */
import {
  SText,
  SCodeSelect,
  SDateField,
  SFrame,
  SLabel,
  SNumberField,
  STextField,
  SFileButton,
  msgState,
  SConfirmDialog,
} from "components";
import { getSCode, OutputFormat, SCode, SeikyusyoType } from "messages/codes";
import { labels } from "messages/labels";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { focusState, useFocus } from "states";
import { date, download, printed } from "services";
import {
  B000140UrikakeshainNm,
  B000150SeikyuNmFrom,
  B000150SeikyuNmTo,
  B030030Print,
  B030030Request,
  catchFileError,
} from "clients/rest";
import { useNavigate } from "react-router-dom";
import { B000140, B000150 } from "pages";
import { calledParam } from "services/codes";
import { getClientMessage } from "messages";

// 呼び出し時のプロパティ
type Props = Partial<{
  // 呼び出し元へデータを送る場合に設定する。
  handleSubmit: (data: any) => void;
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  seikyushimeYmd: string;
  urikakeshainCd: string;
  urikakeshainNm: string;
  seikyuCdFrom: string;
  seikyushitenCdFrom: string;
  seikyuNmFrom: string;
  seikyuCdTo: string;
  seikyushitenCdTo: string;
  seikyuNmTo: string;
  seikyushoType: SCode;
  outputFormat: SCode;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  seikyushimeYmd: date(),
  urikakeshainCd: "",
  urikakeshainNm: "",
  seikyuCdFrom: "",
  seikyushitenCdFrom: "",
  seikyuNmFrom: "",
  seikyuCdTo: "",
  seikyushitenCdTo: "",
  seikyuNmTo: "",
  seikyushoType: getSCode(SeikyusyoType, "1"),
  outputFormat: getSCode(OutputFormat, "1"),
};

const focusId = "B030030";

export const B030030 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};
const Page = (props: Props) => {
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));

  // 初期表示イベント
  useEffect(() => {
    focus(1);
  });

  // エラー表示
  const setMsg = useSetRecoilState(msgState);

  // 画面遷移
  const navigate = useNavigate();

  //Excel出力ボタンクリックイベント
  const handleClickExcelButton = () => {
    // Excel出力フォームのデータを取得する。
    const fields = methods.getValues();

    // Excel出力パラメータを作成する。
    const param: B030030Request = {
      seikyushimeYmd: fields.seikyushimeYmd!,
      seikyushoType: fields.seikyushoType?.value!,
      outputFormat: fields.outputFormat?.value!,
      urikakeshainCd: fields.urikakeshainCd,
      seikyuCdFrom: fields.seikyuCdFrom,
      seikyushitenCdFrom: fields.seikyushitenCdFrom,
      seikyuCdTo: fields.seikyuCdTo,
      seikyushitenCdTo: fields.seikyushitenCdTo,
    };
    B030030Print(param)
      .then((response) => {
        if (printed(response)) {
          setMsg(getClientMessage("B030030_001"));
        } else {
          download(response);
          setMsg(getClientMessage("B030030_002"));
        }
      })
      .catch((reason) => {
        catchFileError(reason, navigate, setMsg);
      });
    focus(1);
  };

  // レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame
        title="請求書出力"
        gridTemplateColumns="7rem 5rem 5rem 15rem 1rem 5rem 5rem 15rem"
      >
        <SText gridColumn="1">＜出力対象＞</SText>
        <SLabel gridColumn="1" required>
          請求締日
        </SLabel>
        <SDateField
          name="seikyushimeYmd"
          label="請求締日"
          required
          gridColumn="span 2"
          {...register(1)}
        />

        <SLabel gridColumn="1">売掛担当者</SLabel>
        <SNumberField
          name="urikakeshainCd"
          label="売掛担当者コード"
          code
          size={7}
          gridColumn="span 2"
          onBlur={() => B000140UrikakeshainNm(methods, navigate, setMsg)}
          dialog={<B000140 />}
          handleSubmit={(data: any) => {
            methods.setValue("urikakeshainCd", data.shainCd);
            methods.setValue("urikakeshainNm", data.shainNm);
          }}
          {...register(2)}
        />
        <STextField name="urikakeshainNm" label="売掛担当者名" disabled />

        <SLabel gridColumn="1">請求先</SLabel>
        <SNumberField
          name="seikyuCdFrom"
          label="請求先コードの範囲(開始)"
          code
          size={6}
          onBlur={() => B000150SeikyuNmFrom(methods, navigate, setMsg)}
          {...register(3)}
        />
        <SNumberField
          name="seikyushitenCdFrom"
          label="請求先支店コードの範囲(開始)"
          code
          size={4}
          dialog={<B000150 calledParam={calledParam.seikyu} />}
          handleSubmit={(data: any) => {
            methods.setValue("seikyuCdFrom", data.tokuiCd);
            methods.setValue("seikyushitenCdFrom", data.tokuishitenCd);
            methods.setValue("seikyuNmFrom", data.tokuiNmr);
          }}
          onBlur={() => B000150SeikyuNmFrom(methods, navigate, setMsg)}
          {...register(4)}
        />
        <STextField
          name="seikyuNmFrom"
          label="請求先名の範囲(開始)"
          size={24}
          disabled
        />

        <SText>～</SText>

        <SNumberField
          name="seikyuCdTo"
          label="請求先コードの範囲(終了)"
          code
          size={6}
          onBlur={() => B000150SeikyuNmTo(methods, navigate, setMsg)}
          {...register(5)}
        />
        <SNumberField
          name="seikyushitenCdTo"
          label="請求先コードの範囲(終了)"
          code
          size={4}
          dialog={<B000150 calledParam={calledParam.seikyu} />}
          handleSubmit={(data: any) => {
            methods.setValue("seikyuCdTo", data.tokuiCd);
            methods.setValue("seikyushitenCdTo", data.tokuishitenCd);
            methods.setValue("seikyuNmTo", data.tokuiNmr);
          }}
          onBlur={() => B000150SeikyuNmTo(methods, navigate, setMsg)}
          {...register(6)}
        />
        <STextField
          name="seikyuNmTo"
          label="請求先名の範囲(終了)"
          size={24}
          disabled
          gridColumn="span 1"
        />

        <SText gridColumn="1">　</SText>

        <SText gridColumn="1">＜出力形式＞</SText>
        <SLabel gridColumn="1" required>
          請求書タイプ
        </SLabel>
        <SCodeSelect
          name="seikyushoType"
          label="請求書タイプ"
          required
          gridColumn="span 2"
          options={SeikyusyoType}
          {...register(7)}
        />
        <SLabel gridColumn="1" required>
          出力形式
        </SLabel>
        <SCodeSelect
          name="outputFormat"
          label="出力形式"
          required
          gridColumn="span 2"
          options={OutputFormat}
          {...register(8)}
        />
        <SFileButton
          label={labels.buttons.output}
          gridColumn={8}
          align="right"
          {...register(23)}
          dialog={<SConfirmDialog title="印刷" msg="請求書を印刷しますか？" />}
          dialogAction="confirm"
          handleSubmit={handleClickExcelButton}
        />
      </SFrame>
    </FormProvider>
  );
};
